import Head from 'next/head';
import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import {
  buttonSecondary,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import { useB2bTranslation } from './kit';

type Faq = { question: string; answer: string };

export default function TeamsFaq() {
  const { t } = useB2bTranslation();

  const faqs: Faq[] = [
    {
      question: t('How do seats work?'),
      answer: t(
        'Each person on your team gets a seat with their own login. Plans include 3 to 30 seats, and the plan owner can add or remove seats from the team settings at any time.',
      ),
    },
    {
      question: t('Does the whole team share one allowance?'),
      answer: t(
        'No. Every person gets their own monthly allowance of premium words, image and video credits, text to speech and transcriptions, so one busy teammate never uses up everyone else’s.',
      ),
    },
    {
      question: t('What can my team share?'),
      answer: t(
        'Chats, prompts, brand voices, custom tools, AI agents, images, videos, transcriptions and workflows. You can even share a prompt while keeping its text hidden.',
      ),
    },
    {
      question: t('Is our data used to train AI models?'),
      answer: t(
        'We don’t use your inputs to train our own models. Each request is processed by the AI provider behind the model you choose, under that provider’s terms.',
      ),
    },
    {
      question: t('Which security standards do you meet?'),
      answer: t(
        'Easy-Peasy.AI is SOC 2, ISO 27001, GDPR and HIPAA compliant. You’ll find our policies and security documentation in our Trust Center.',
      ),
    },
    {
      question: t('Can we sign a BAA for HIPAA?'),
      answer: t(
        'Yes. On any paid plan, you can sign our Business Associate Agreement from your account settings.',
      ),
    },
    {
      question: t('Can Easy-Peasy.AI work with our other tools?'),
      answer: t(
        'Yes. Marky connects to apps like Gmail, Google Drive, Outlook, Notion, Asana and Stripe, and every Teams plan includes API access and our MCP server.',
      ),
    },
    {
      question: t('Do you offer training for our team?'),
      answer: t(
        'Yes. We run hands-on workshops for companies, built around your goals and the work your team does. Tell us about your team and we’ll plan one together.',
      ),
    },
    {
      question: t('What if we need more than 30 seats?'),
      answer: t(
        'Talk to us and we’ll put together a plan with the seats, pricing and onboarding your organization needs.',
      ),
    },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <section id='faq' className='py-20 sm:py-28'>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading
            size='md'
            title={t('Questions from teams.')}
            lead={t(
              'Anything else you’d like to know before you bring your team over? We’re happy to help.',
            )}
          />
          <Link href='/contact' className={clsxm(buttonSecondary, 'mt-8')}>
            {t('Talk to sales')}
          </Link>
        </div>

        <div className='lg:col-span-8'>
          <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] dark:divide-white/[0.08] dark:border-white/[0.08]'>
            {faqs.map(({ question, answer }) => (
              <li key={question}>
                <details className='group'>
                  <summary
                    className={clsxm(
                      'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start text-lg font-semibold transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                      tone.ink,
                    )}
                  >
                    {question}
                    <span
                      aria-hidden='true'
                      className={clsxm(
                        'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                        tone.line,
                      )}
                    >
                      <span className='absolute h-px w-3 bg-current' />
                      <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                    </span>
                  </summary>
                  <p
                    className={clsxm(
                      'max-w-2xl pb-7 pe-12 text-[16px] leading-7',
                      tone.body,
                    )}
                  >
                    {answer}
                  </p>
                </details>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
